/**
 * Choosing where civilisations begin.
 *
 * A start position is not judged by the tile a settler stands on but by the tiles the
 * city it founds will WORK. So each candidate is scored over the ring a city can
 * reach, weighting food above production above gold, rewarding fresh water and a
 * coastline, and penalising a start that is mostly sea.
 *
 * The Phase 3 version of this file picked any land tile on the largest continent and
 * was honest about being a placeholder. This one is the real thing, and it matters
 * more than it looks: a civilisation that opens on three tiles of tundra never
 * recovers, and no amount of good play later can fix it.
 *
 * Deliberately deterministic — no randomness beyond the map itself. The same world
 * always yields the same starts, which is what lets a reported game be reproduced from
 * its seed alone.
 */

import type { Content } from '../../../content/schema/index.js';
import { hexDistance, hexSpiral, offsetToAxial, type Hex } from '../hex/index.js';
import { findRegions } from '../map/hydrology.js';
import { hexIndex, indexToHex, indexToOffset, neighborIndices } from './grid.js';
import type { TileGrid } from './types.js';

export interface ScoredStart {
  index: number;
  score: number;
}

/** Combined yield of a tile as it stands: terrain, plus feature, plus resource. */
function tileScore(map: TileGrid, content: Content, index: number): number {
  const rules = content.rules.start;
  const terrain = content.terrain.get(map.terrain[index]!);
  const feature = content.features.get(map.feature[index]!);
  const resource = content.resources.get(map.resource[index]!);

  const food = terrain.yields.food + feature.yields.food + resource.yields.food;
  const production = terrain.yields.production + feature.yields.production + resource.yields.production;
  const gold = terrain.yields.gold + feature.yields.gold + resource.yields.gold;

  let score = food * rules.foodWeight + production * rules.productionWeight + gold * rules.goldWeight;
  if (resource.key !== 'none') score += rules.resourceBonus;
  if (terrain.water) score -= rules.waterTilePenalty;
  return score;
}

/** Whether this tile has fresh water — a river running through it, or a lake beside it. */
function hasFreshWater(map: TileGrid, content: Content, index: number): boolean {
  if (map.rivers[index] !== 0) return true;
  const lake = content.terrain.id('lake');
  return neighborIndices(map, index).some((n) => map.terrain[n] === lake);
}

function isCoastal(map: TileGrid, content: Content, index: number): boolean {
  return neighborIndices(map, index).some((n) => content.terrain.get(map.terrain[n]!).water);
}

/** Score one candidate over the ring a city founded here would work. */
export function scoreStart(map: TileGrid, content: Content, index: number): number {
  const rules = content.rules.start;
  const centre = indexToHex(map, index);

  let score = 0;
  for (const h of hexSpiral(centre, rules.ringRadius)) {
    const i = hexIndex(map, h);
    if (i === -1) continue;
    score += tileScore(map, content, i);
  }

  // The centre tile is worked for free and for the whole game, so it counts twice.
  score += tileScore(map, content, index);

  if (hasFreshWater(map, content, index)) score += rules.freshWaterBonus;
  if (isCoastal(map, content, index)) score += rules.coastBonus;
  return score;
}

/**
 * Pick `count` starting tiles, best first, keeping them apart.
 *
 * Separation is enforced greedily against the positions already taken: the best
 * remaining candidate that is far enough from all of them. Greedy rather than optimal
 * because the failure mode of optimal placement is every civilisation getting an
 * identically mediocre start, which is worse than one of them getting a good one.
 */
export function chooseStartPositions(map: TileGrid, content: Content, count: number): number[] {
  const rules = content.rules.start;
  const tiles = map.width * map.height;
  const land = new Uint8Array(tiles);

  for (let i = 0; i < tiles; i++) {
    const terrain = content.terrain.get(map.terrain[i]!);
    if (!terrain.water && terrain.passable) land[i] = 1;
  }

  const { region, sizes } = findRegions(map, land, 1);
  if (sizes.length === 0) throw new Error('This world has no land a civilisation could start on.');

  // Only landmasses big enough to hold a city and grow into. A civilisation stranded
  // on a four-tile island is not a start, it is a loss.
  const minimumLandmass = Math.max(8, Math.round(rules.ringRadius * rules.ringRadius * 3));
  const minRow = Math.floor(map.height * rules.polarMargin);
  const maxRow = Math.ceil(map.height * (1 - rules.polarMargin));

  const candidates: ScoredStart[] = [];
  for (let i = 0; i < tiles; i++) {
    if (land[i] !== 1) continue;
    if (sizes[region[i]!]! < minimumLandmass) continue;
    const row = (i - (i % map.width)) / map.width;
    if (row < minRow || row > maxRow) continue;
    candidates.push({ index: i, score: scoreStart(map, content, i) });
  }

  if (candidates.length === 0) {
    // Every landmass is tiny or polar. Fall back to any land at all rather than fail.
    for (let i = 0; i < tiles; i++) if (land[i] === 1) candidates.push({ index: i, score: 0 });
  }
  if (candidates.length === 0) throw new Error('This world has no land a civilisation could start on.');

  // Highest score first; ties by tile index, so the order never depends on the order
  // the candidates happened to be collected in.
  candidates.sort((a, b) => (b.score - a.score) || (a.index - b.index));

  const chosen: number[] = [];
  for (let separation = rules.minSeparation; separation >= 0 && chosen.length < count; separation--) {
    for (const candidate of candidates) {
      if (chosen.length >= count) break;
      if (chosen.includes(candidate.index)) continue;
      const farEnough = chosen.every(
        (other) => wrappedDistance(map, candidate.index, other) >= separation,
      );
      if (farEnough) chosen.push(candidate.index);
    }
    // If the map is too crowded to honour the separation, the loop relaxes it by one
    // and tries again rather than returning fewer civilisations than were asked for.
  }

  return chosen;
}

/**
 * Hex distance between two tiles that knows the world may be a cylinder.
 *
 * Without the lap check, two starts either side of the date line look like opposite
 * ends of the world and the separation rule waves them through.
 */
export function wrappedDistance(map: TileGrid, from: number, to: number): number {
  const a: Hex = indexToHex(map, from);
  const b = indexToOffset(map, to);

  let best = hexDistance(a, offsetToAxial(b.col, b.row));
  if (map.wrapX) {
    for (const lap of [-1, 1]) {
      const d = hexDistance(a, offsetToAxial(b.col + lap * map.width, b.row));
      if (d < best) best = d;
    }
  }
  return best;
}
